import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;

  if (variant === 'compact') {
    return (
      <article>
        <Link to={`/article/${article.id}`} className="group block">
          <div className="flex items-start gap-3">
            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
              <img
                src={article.imageUrlThumb}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-primary-500 font-label">{categoryName}</span>
              <h3 className="text-sm font-heading font-medium text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-500 transition-colors">
                {article.title}
              </h3>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'featured') {
    return (
      <article>
        <Link to={`/article/${article.id}`} className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer">
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={article.imageUrlFeature}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs text-primary-500 font-label bg-primary-50 px-2 py-0.5 rounded-full">
                {categoryName}
              </span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
              <span>{article.date}</span>
              <span>{article.author}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article>
      <Link to={`/article/${article.id}`} className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer">
        <div className="aspect-[3/2] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-primary-500 font-label bg-primary-50 px-2 py-0.5 rounded-full">
              {categoryName}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.author}</span>
          </div>
        </div>
      </Link>
    </article>
  );
}